import React, { useRef, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  ChartContainer,
  BarPlot,
  LinePlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsGrid,
  ChartsLegend,
  ChartsReferenceLine,
} from "@mui/x-charts";
import { formatChartTitle, getMetricUnit } from "../../utils";
import { ChartMetricTooltip } from "./ChartMetricTooltip";

interface CombinedBarLineChartData {
  [key: string]: string | number | null | undefined;
}

interface BarSeries {
  dataKey: string;
  label: string;
  color?: string;
}

interface LineSeries {
  dataKey: string;
  label: string;
  color?: string;
}

interface CombinedBarLineChartProps {
  data: CombinedBarLineChartData[];
  xAxisKey: string;
  barSeries: BarSeries[];
  lineSeries: LineSeries[];
  title?: string;
  height?: number;
  barYAxisMin?: number;
  barYAxisMax?: number;
  lineYAxisMin?: number;
  lineYAxisMax?: number;
  showMinMaxLines?: boolean;
  /**
   * When provided, used as the min reference line for the bar axis instead of
   * computing it from the displayed (averaged) bar series data.
   */
  referenceBarMin?: number;
  /**
   * When provided, used as the max reference line for the bar axis instead of
   * computing it from the displayed (averaged) bar series data.
   */
  referenceBarMax?: number;
}

/**
 * Calculates a sensible tick step for a Y-axis given the data range.
 * Aims for approximately 5 ticks within the range.
 */
function calculateTickStep(min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 1;
  const rawStep = range / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceStep: number;
  if (normalized <= 1) niceStep = 1;
  else if (normalized <= 2) niceStep = 2;
  else if (normalized <= 5) niceStep = 5;
  else niceStep = 10;
  return niceStep * magnitude;
}

export const CombinedBarLineChart: React.FC<CombinedBarLineChartProps> = ({
  data,
  xAxisKey,
  barSeries,
  lineSeries,
  title,
  height = 300,
  barYAxisMin,
  barYAxisMax,
  lineYAxisMin,
  lineYAxisMax,
  showMinMaxLines = false,
  referenceBarMin,
  referenceBarMax,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const formattedTitle = formatChartTitle(title);

  // Compute actual data min/max for bar series for dynamic scaling
  const { barDataMin, barDataMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const item of data) {
      for (const s of barSeries) {
        const val = item[s.dataKey];
        if (typeof val === "number" && isFinite(val)) {
          if (val < min) min = val;
          if (val > max) max = val;
        }
      }
    }
    return {
      barDataMin: min === Infinity ? 0 : min,
      barDataMax: max === -Infinity ? 100 : max,
    };
  }, [data, barSeries]);

  // Compute actual data min/max for line series
  const { lineDataMin, lineDataMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const item of data) {
      for (const s of lineSeries) {
        const val = item[s.dataKey];
        if (typeof val === "number" && isFinite(val)) {
          if (val < min) min = val;
          if (val > max) max = val;
        }
      }
    }
    return {
      lineDataMin: min === Infinity ? 0 : min,
      lineDataMax: max === -Infinity ? 100 : max,
    };
  }, [data, lineSeries]);

  // Effective bounds with padding.
  // When showMinMaxLines is active the bar axis must extend at least to the
  // reference line values plus a small buffer so the labels are never clipped.
  const effectiveBarYMin = useMemo(() => {
    const range = barDataMax - barDataMin;
    const padding = Math.max(range * 0.05, 1);
    let base: number;
    if (barYAxisMin !== undefined) {
      base = barYAxisMin;
    } else {
      base = Math.max(0, Math.floor(barDataMin - padding));
    }
    if (showMinMaxLines && referenceBarMin !== undefined) {
      base = Math.min(base, Math.floor(referenceBarMin - padding));
    }
    return base;
  }, [barYAxisMin, barDataMin, barDataMax, showMinMaxLines, referenceBarMin]);

  const effectiveBarYMax = useMemo(() => {
    const range = barDataMax - barDataMin;
    const padding = Math.max(range * 0.05, 1);
    let base: number;
    if (barYAxisMax !== undefined) {
      base = barYAxisMax;
    } else {
      base = Math.ceil(barDataMax + padding);
    }
    if (showMinMaxLines) {
      const refMaxValue =
        referenceBarMax !== undefined ? referenceBarMax : barDataMax;
      base = Math.max(base, Math.ceil(refMaxValue + padding));
    }
    return base;
  }, [barYAxisMax, barDataMin, barDataMax, showMinMaxLines, referenceBarMax]);

  const effectiveLineYMin = useMemo(() => {
    if (lineYAxisMin !== undefined) return lineYAxisMin;
    const range = lineDataMax - lineDataMin;
    const padding = Math.max(range * 0.05, 1);
    return Math.max(0, Math.floor(lineDataMin - padding));
  }, [lineYAxisMin, lineDataMin, lineDataMax]);

  const effectiveLineYMax = useMemo(() => {
    if (lineYAxisMax !== undefined) return lineYAxisMax;
    const range = lineDataMax - lineDataMin;
    const padding = Math.max(range * 0.05, 1);
    return Math.ceil(lineDataMax + padding);
  }, [lineYAxisMax, lineDataMin, lineDataMax]);

  const barTickStep = useMemo(
    () => calculateTickStep(effectiveBarYMin, effectiveBarYMax),
    [effectiveBarYMin, effectiveBarYMax]
  );

  const lineTickStep = useMemo(
    () => calculateTickStep(effectiveLineYMin, effectiveLineYMax),
    [effectiveLineYMin, effectiveLineYMax]
  );

  const barChartSeries = barSeries.map((s, index) => ({
    type: "bar" as const,
    dataKey: s.dataKey,
    label: s.label,
    color: s.color || theme.palette.primary.main,
    yAxisId: "bar-axis",
    id: `bar-${index}`,
  }));

  const lineChartSeries = lineSeries.map((s, index) => ({
    type: "line" as const,
    dataKey: s.dataKey,
    label: s.label,
    color: s.color || theme.palette.success.main,
    yAxisId: "line-axis",
    id: `line-${index}`,
  }));

  const allSeries = [...barChartSeries, ...lineChartSeries];

  // Derive a unit per axis from its series labels — shown only when every
  // unit-bearing series on that axis shares the same unit (BT-218).
  const unitFor = (list: { label: string }[]): string => {
    const units = new Set(
      list.map((s) => getMetricUnit(s.label)).filter((u) => u !== "")
    );
    return units.size === 1 ? Array.from(units)[0] : "";
  };
  const barAxisUnit = unitFor(barSeries);
  const lineAxisUnit = unitFor(lineSeries);

  // Use externally-provided reference values (raw data min/max from API) when available,
  // otherwise fall back to computing from the displayed averaged bar series data.
  const refBarMin = referenceBarMin !== undefined ? referenceBarMin : barDataMin;
  const refBarMax = referenceBarMax !== undefined ? referenceBarMax : barDataMax;

  const barMinLabel = `Min: ${Math.round(refBarMin * 10) / 10}`;
  const barMaxLabel = `Max: ${Math.round(refBarMax * 10) / 10}`;

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      {formattedTitle && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <ChartContainer
        dataset={data}
        height={height}
        series={allSeries}
        xAxis={[
          {
            scaleType: "band",
            dataKey: xAxisKey,
            disableTicks: true,
          },
        ]}
        yAxis={[
          {
            id: "bar-axis",
            min: effectiveBarYMin,
            max: effectiveBarYMax,
            disableTicks: true,
            tickMinStep: barTickStep,
          },
          {
            id: "line-axis",
            min: effectiveLineYMin,
            max: effectiveLineYMax,
            disableTicks: true,
            tickMinStep: lineTickStep,
            position: "right",
          },
        ]}
        margin={{ left: barAxisUnit ? 16 : 0, right: lineAxisUnit ? 16 : 0, top: 10, bottom: 80 }}
      >
        <ChartsGrid vertical horizontal />
        <BarPlot borderRadius={12} />
        <LinePlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="bar-axis" label={barAxisUnit || undefined} />
        <ChartsYAxis axisId="line-axis" label={lineAxisUnit || undefined} />
        <ChartsLegend />
        <ChartMetricTooltip />
        {showMinMaxLines && data.length > 0 && refBarMin !== refBarMax && (
          <>
            <ChartsReferenceLine
              axisId="bar-axis"
              y={refBarMax}
              label={barMaxLabel}
              labelAlign="end"
              lineStyle={{
                stroke: theme.palette.text.secondary,
                strokeDasharray: "6 3",
                strokeWidth: 1.5,
                opacity: 0.7,
              }}
              labelStyle={{
                fontSize: 11,
                fill: theme.palette.text.secondary,
              }}
            />
            <ChartsReferenceLine
              axisId="bar-axis"
              y={refBarMin}
              label={barMinLabel}
              labelAlign="end"
              lineStyle={{
                stroke: theme.palette.text.secondary,
                strokeDasharray: "6 3",
                strokeWidth: 1.5,
                opacity: 0.7,
              }}
              labelStyle={{
                fontSize: 11,
                fill: theme.palette.text.secondary,
              }}
            />
          </>
        )}
      </ChartContainer>
    </Box>
  );
};
